// src/context/AIContext.js
import React, { createContext, useState, useContext, useCallback, useEffect } from 'react';
import { aiApi } from '../utils/aiApi';

const AIContext = createContext();

export const useAI = () => {
  const context = useContext(AIContext);
  if (!context) {
    throw new Error('useAI must be used within an AIProvider');
  }
  return context;
};

export const AIProvider = ({ children }) => {
  const [sessionId, setSessionId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [aiStatus, setAiStatus] = useState('checking');

  // Initialize AI session
  const initializeSession = useCallback(async () => {
    try {
      const { session_id } = await aiApi.createSession();
      setSessionId(session_id);
      
      // Store session in localStorage for persistence
      localStorage.setItem('brasotech_ai_session', session_id);
      
      return session_id;
    } catch (error) {
      console.error('Failed to initialize AI session:', error);
      return null;
    }
  }, []);

  // Check AI health
  const checkHealth = useCallback(async () => {
    try {
      const health = await aiApi.checkHealth();
      setAiStatus(health.status);
      return health;
    } catch (error) {
      setAiStatus('unavailable');
      return { status: 'unavailable' };
    }
  }, []);

  // Ask AI a question
  const askQuestion = useCallback(async (question) => {
    if (!question.trim()) {
      throw new Error('Question cannot be empty');
    }

    setIsLoading(true);
    try {
      const currentSessionId = sessionId || await initializeSession();
      const response = await aiApi.askQuestion(question, currentSessionId);
      
      // Update session ID if new one was created
      if (response.session_id && response.session_id !== sessionId) {
        setSessionId(response.session_id);
        localStorage.setItem('brasotech_ai_session', response.session_id);
      }
      
      return response;
    } catch (error) {
      console.error('Error asking AI:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [sessionId, initializeSession]);

  // Clear chat history
  const clearChat = useCallback(async () => {
    if (sessionId) {
      try {
        await aiApi.clearSession(sessionId);
      } catch (error) {
        console.error('Error clearing session:', error);
      }
    }
    
    // Create new session
    const newSessionId = await initializeSession();
    return newSessionId;
  }, [sessionId, initializeSession]);

  // Initialize on mount
  useEffect(() => {
    const initialize = async () => {
      // Check health first
      await checkHealth();
      
      // Try to restore session from localStorage
      const savedSessionId = localStorage.getItem('brasotech_ai_session');
      if (savedSessionId) {
        setSessionId(savedSessionId);
      } else {
        // Create new session
        await initializeSession();
      }
    };

    initialize();
  }, [initializeSession, checkHealth]);

  const value = {
    askQuestion,
    clearChat,
    isLoading,
    aiStatus,
    sessionId,
    checkHealth,
  };

  return (
    <AIContext.Provider value={value}>
      {children}
    </AIContext.Provider>
  );
};